import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import * as cartApi from "@/lib/adapters/cart";
import { getProduct } from "@/lib/adapters/products";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);

const LS_KEY = "dyntra_cart_v1";
const EMPTY = { items: [], subtotal: 0, count: 0 };

function readLocalCart() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : { items: [] };
  } catch { return { items: [] }; }
}
function writeLocalCart(items) {
  localStorage.setItem(LS_KEY, JSON.stringify({ items }));
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState(EMPTY);
  const [productCache, setProductCache] = useState({});

  const hydrateLocal = useCallback(async () => {
    const raw = readLocalCart();
    if (!raw.items?.length) { setCart(EMPTY); return; }
    const missing = raw.items.filter((i) => !productCache[i.product_id]).map((i) => i.product_id);
    const cache = { ...productCache };
    if (missing.length) {
      const results = await Promise.all(
        missing.map((id) => getProduct(id).catch(() => null)),
      );
      results.forEach((p, i) => { if (p) cache[missing[i]] = p; });
      setProductCache(cache);
    }
    let subtotal = 0;
    const items = raw.items
      .map((i) => {
        const p = cache[i.product_id];
        if (!p) return null;
        const line = p.price * i.quantity;
        subtotal += line;
        return { product: p, quantity: i.quantity, line_total: line };
      })
      .filter(Boolean);
    setCart({ items, subtotal, count: items.reduce((n, i) => n + i.quantity, 0) });
  }, [productCache]);

  const refresh = useCallback(async () => {
    if (user) {
      try {
        const summary = await cartApi.getCart();
        setCart(summary);
      } catch { /* ignore */ }
    } else {
      await hydrateLocal();
    }
  }, [user, hydrateLocal]);

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [user]);

  // Push any pending local cart items to the server cart, clear local storage,
  // and re-fetch the server summary. Exposed so callers (e.g. Checkout's
  // in-flow OTP step) can `await` migration deterministically instead of
  // relying on the auto-migration useEffect below.
  const syncLocalToServer = useCallback(async () => {
    const raw = readLocalCart();
    if (!raw.items?.length) return;
    for (const it of raw.items) {
      try { await cartApi.addToCart(it.product_id, it.quantity); } catch {}
    }
    writeLocalCart([]);
    await refresh();
  }, [refresh]);

  useEffect(() => {
    if (!user) return;
    syncLocalToServer();
  }, [user, syncLocalToServer]);

  const addToCart = async (productId, quantity = 1) => {
    if (user) {
      try {
        const summary = await cartApi.addToCart(productId, quantity);
        setCart(summary);
      } catch { toast.error("Failed to add to cart"); }
    } else {
      const raw = readLocalCart();
      const existing = raw.items.find((i) => i.product_id === productId);
      if (existing) existing.quantity += quantity;
      else raw.items.push({ product_id: productId, quantity });
      writeLocalCart(raw.items);
      await hydrateLocal();
    }
  };

  const updateQty = async (productId, quantity) => {
    if (user) {
      try {
        const summary = await cartApi.updateQty(productId, quantity);
        setCart(summary);
      } catch { toast.error("Could not update quantity"); }
    } else {
      const raw = readLocalCart();
      if (quantity <= 0) raw.items = raw.items.filter((i) => i.product_id !== productId);
      else {
        const it = raw.items.find((i) => i.product_id === productId);
        if (it) it.quantity = quantity;
      }
      writeLocalCart(raw.items);
      await hydrateLocal();
    }
  };

  const removeItem = async (productId) => {
    if (user) {
      try {
        const summary = await cartApi.removeItem(productId);
        setCart(summary);
      } catch { toast.error("Could not remove item"); return; }
    } else {
      const raw = readLocalCart();
      raw.items = raw.items.filter((i) => i.product_id !== productId);
      writeLocalCart(raw.items);
      await hydrateLocal();
    }
    toast.success("Removed from cart");
  };

  const clearCart = async () => {
    if (user) {
      try { await cartApi.clearCart(); } catch {}
    }
    writeLocalCart([]);
    setCart(EMPTY);
  };

  return (
    <CartContext.Provider value={{ cart, addToCart, updateQty, removeItem, refresh, clearCart, syncLocalToServer }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
